"use client";

import Image from "next/image";
import NextLink from "next/link";
import { useId, useState } from "react";

import { LIST_COVER_PLACEHOLDER_SRC } from "@/components/brand/list-cover-placeholder";
import { IMAGE_QUALITY, IMAGE_SIZES_PRESETS } from "@/lib/images/image-config";
import { cn } from "@/lib/utils";

interface ListCardBaseProps {
  /** The kind of list: the event type ("Casamento") or "Lista de sugestão" (canon §13). */
  eyebrow: string;
  /** The couple's names or the curation's name. */
  title: string;
  /** Date and city, or the curation's description. Omitted when empty. */
  supportLine?: string;
  /** The list's photograph; `null` wears the brand's cover placeholder. */
  coverSrc: string | null;
  /**
   * Serve the photograph through `next/image`. Only for addresses the image optimiser is
   * configured for (the house's own suggestion photographs); list covers live on a bucket that
   * varies per environment and go out as they are.
   */
  optimizeCover?: boolean;
  /** Extra classes on the card's root, for the grid or row that places it. */
  className?: string;
}

/** The default card: the frame is a link to `href`. */
interface ListCardLinkProps extends ListCardBaseProps {
  href: string;
  /** Called when the card's link is pressed; navigation is left alone. Where the analytics hear of it. */
  onNavigate?: () => void;
  onSelect?: never;
}

/**
 * The card as an in-page trigger (the backoffice's search opens a drawer instead of leaving the
 * page): the frame is a `<button type="button">` and hands itself to `onSelect`, so the caller
 * can return focus to it once whatever it opened closes.
 */
interface ListCardSelectProps extends ListCardBaseProps {
  onSelect: (trigger: HTMLElement) => void;
  href?: never;
}

export type ListCardProps = ListCardLinkProps | ListCardSelectProps;

/** The frame's focus ring, the same on the link and on the button. */
const FRAME_CLASS =
  "block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2";

/**
 * Undoes what a native button adds on top of the preflight (shrink-to-fit width, centred text,
 * the default cursor), so the button frame wears the link frame's shell untouched.
 */
const BUTTON_FRAME_RESET_CLASS = "w-full cursor-pointer appearance-none text-left";

/**
 * The one card for every piece that represents a list (canon §13): search results, the
 * suggestion lists on the homepage and in the creation's choice. A 4:5 frame in porcelain while
 * the photograph is on its way, the list's photograph in cover (or the brand's placeholder when
 * there is none, or when it fails to load), the green hover wash, the scrim, and the words on
 * the photograph. The whole frame is the one link (or, with `onSelect`, the one button): no
 * support link outside it.
 *
 * The photograph bleeds 8px past the card's box (`.tb-public-list-card` gives the 8px inline
 * padding, the surface takes it back), so the card's container adds no padding of its own.
 */
export function ListCard(props: ListCardProps) {
  const { eyebrow, title, supportLine, coverSrc, optimizeCover = false, className } = props;
  const titleId = useId();
  // A cover that fails to load is a list with no cover: it wears the placeholder instead.
  const [coverFailed, setCoverFailed] = useState(false);
  const [coverLoaded, setCoverLoaded] = useState(false);
  const hasCover = coverSrc !== null && !coverFailed;
  // Porcelain while the photograph is on its way; the placeholder only when there is none.
  const coverState = !hasCover ? "none" : coverLoaded ? "loaded" : "pending";

  const surface = (
    <div className="tb-public-list-card-surface" data-cover={coverState}>
      {coverState === "none" ? (
        // eslint-disable-next-line @next/next/no-img-element -- a static brand SVG, no optimisation to gain
        <img
          src={LIST_COVER_PLACEHOLDER_SRC}
          alt=""
          aria-hidden="true"
          className="tb-public-list-card-cover"
          data-list-placeholder=""
        />
      ) : null}
      {hasCover && optimizeCover ? (
        // The name is written on the card, so the photograph is decorative.
        <Image
          src={coverSrc}
          alt=""
          fill
          sizes={IMAGE_SIZES_PRESETS.grid3}
          quality={IMAGE_QUALITY.default}
          onLoad={() => setCoverLoaded(true)}
          onError={() => setCoverFailed(true)}
          className="tb-public-list-card-cover"
        />
      ) : null}
      {hasCover && !optimizeCover ? (
        // eslint-disable-next-line @next/next/no-img-element -- list covers live on a bucket that varies per environment, unconfigured for next/image
        <img
          // A cover already in the cache can finish before hydration attaches `onLoad`.
          ref={(image) => {
            if (image?.complete && image.naturalWidth > 0) setCoverLoaded(true);
          }}
          src={coverSrc}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setCoverLoaded(true)}
          onError={() => setCoverFailed(true)}
          className="tb-public-list-card-cover"
        />
      ) : null}
      <span aria-hidden="true" className="tb-public-list-card-wash" />
      <span aria-hidden="true" className="tb-public-list-card-scrim" />
      <div className="tb-public-list-card-content">
        <p className="tb-public-list-card-eyebrow">{eyebrow}</p>
        <h3 id={titleId}>{title}</h3>
        {supportLine === undefined || supportLine === "" ? null : (
          <p className="tb-public-list-card-date">{supportLine}</p>
        )}
      </div>
    </div>
  );

  return (
    <article aria-labelledby={titleId} className={cn("tb-public-list-card", className)}>
      {props.onSelect === undefined ? (
        <NextLink href={props.href} onClick={props.onNavigate} className={FRAME_CLASS}>
          {surface}
        </NextLink>
      ) : (
        <button
          type="button"
          onClick={(event) => props.onSelect(event.currentTarget)}
          className={cn(FRAME_CLASS, BUTTON_FRAME_RESET_CLASS)}
        >
          {surface}
        </button>
      )}
    </article>
  );
}
